import React from 'react';
import PropTypes from 'prop-types';
import Pressable from "react-native-web/dist/exports/Pressable";
import Text from "react-native-web/dist/exports/Text";
import Platform from "react-native-web/dist/exports/Platform";
import { applyTextStyles, useThemeTokensCallback } from '../../ThemeProvider';
import { selectSystemProps, a11yProps, pressProps, viewProps, textProps, getTokensPropType, variantProp, resolvePressableState } from '../../utils';
import { jsx as _jsx } from "react/jsx-runtime";
const [selectPressProps, pressPropTypes] = selectSystemProps([a11yProps, pressProps, viewProps]);
const [selectTextProps, textPropTypes] = selectSystemProps([textProps]);
const selectContainerStyles = _ref => {
  let {
    paddingLeft,
    paddingRight,
    paddingTop,
    paddingBottom = 0,
    borderBottomColor,
    borderBottomWidth = 0,
    borderBottomStyle,
    flex,
    alignItems,
    justifyContent
  } = _ref;
  return {
    paddingLeft,
    paddingRight,
    paddingTop,
    paddingBottom: paddingBottom - borderBottomWidth,
    borderBottomColor,
    borderBottomWidth,
    borderBottomStyle,
    flex,
    alignItems,
    justifyContent,
    ...Platform.select({
      // Removes the default browser :focus outline
      web: {
        outline: 'none'
      }
    })
  };
};
const selectTextStyles = _ref2 => {
  let {
    fontSize,
    fontScaleCap,
    lineHeight,
    letterSpacing,
    fontWeight,
    fontName,
    color
  } = _ref2;
  return applyTextStyles({
    fontSize,
    fontScaleCap,
    lineHeight,
    letterSpacing,
    fontWeight,
    fontName,
    color
  });
};
const CarouselTabsPanelItem = /*#__PURE__*/React.forwardRef((_ref3, ref) => {
  let {
    title,
    selected,
    inactive,
    variant,
    tokens,
    accessibilityRole = 'tab',
    ...rest
  } = _ref3;
  // Workaround for React Native Web https://github.com/necolas/react-native-web/issues/2357
  // Don't allow disabled to be set while focus is true else focus state gets locked `true`
  // (must refocus _after_ calling `goTo`, else focus target content is not up to date)
  const [isFocused, setIsFocused] = React.useState(false);
  const disabled = (inactive || selected) && !isFocused;
  const getTokens = useThemeTokensCallback('CarouselTabsPanelItem', tokens, variant);
  const resolveTokens = pressState => getTokens(resolvePressableState(pressState, {
    selected
  }));
  const getContainerStyle = pressState => selectContainerStyles(resolveTokens(pressState));
  const getTextStyle = pressState => selectTextStyles(resolveTokens(pressState));
  const {
    onPress,
    ...selectedPressProps
  } = selectPressProps(rest);
  const handleKeyDown = event => {
    // Allow using the spacebar for navigation
    if (event?.key === ' ') onPress(event);
  };
  return /*#__PURE__*/_jsx(Pressable, {
    onPress: onPress,
    style: getContainerStyle,
    accessibilityRole: accessibilityRole,
    ref: ref,
    onKeyDown: handleKeyDown,
    onFocus: () => setIsFocused(true),
    onBlur: () => setIsFocused(false),
    disabled: disabled,
    ...selectedPressProps,
    children: pressState => /*#__PURE__*/_jsx(Text, {
      style: getTextStyle(pressState),
      ...selectTextProps(rest),
      children: title
    })
  });
});
CarouselTabsPanelItem.displayName = 'CarouselTabsPanelItem';
CarouselTabsPanelItem.propTypes = {
  ...pressPropTypes,
  ...textPropTypes,
  title: PropTypes.string.isRequired,
  selected: PropTypes.bool,
  tokens: getTokensPropType('CarouselTabsPanelItem'),
  variant: variantProp.propType
};
export default CarouselTabsPanelItem;